import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CommonImageModal from 'components/commonImageModal/CommonImageModal';
import { useDispatch } from 'react-redux';
import { truncateComment } from 'utils/utils';
import { toast } from 'react-toastify';
import { addTollCharge, updateTollCharge, deleteTollCharge } from 'redux/actions/reservationAction';
import { getTollChargesService } from 'services/Reservation';
import './ReservationAttachments.scss';

// Receipts may be photos or scans/statements (pipeline item 4, 2026-09-23):
// JPG, PNG, WebP, HEIC, PDF, DOC, DOCX, XLS, XLSX, up to 10 MB each, any
// number per charge. The backend applies the same list and cap.
const RECEIPT_ACCEPT = '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx';
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
const RECEIPT_EXTENSIONS = RECEIPT_ACCEPT.split(',');
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];

const fileExtension = (name = '') => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
};

// Returns an error message, or "" when the file is acceptable.
const validateReceiptFile = (file) => {
  if (!file) return 'No file selected';
  if (file.size > RECEIPT_MAX_BYTES) return `${file.name}: file size should not exceed 10MB`;
  if (!RECEIPT_EXTENSIONS.includes(fileExtension(file.name))) {
    return `${file.name}: only JPG, PNG, WebP, HEIC, PDF, DOC, DOCX, XLS or XLSX files are allowed`;
  }
  return '';
};

const isImageUrl = (url = '') => IMAGE_EXTENSIONS.includes(fileExtension(url.split('?')[0]));

const ReservationAttachmentsDialog = ({
  open,
  handleClose,
  tripId,
  onSave,
  isInvoiceGenerated = false,
}) => {
  const dispatch = useDispatch();
  const [tollList, setTollList] = useState([]);
  const [parkingList, setParkingList] = useState([]);
  const [previewModal, setPreviewModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [saving, setSaving] = useState(false);

  const [newUploadType, setNewUploadType] = useState(null); // "toll" | "parking"
  const [uploading, setUploading] = useState(false);
  const newUploadInputRef = useRef(null);

  // Uploads and removals refresh only the two lists in this dialog. The
  // booking page behind it is refetched once, on close, and only if something
  // changed: until 2026-09-23 every upload refetched the whole reservation,
  // which flipped the page into its full-screen loader mid-upload.
  const dirtyRef = useRef(false);
  const markDirty = () => {
    dirtyRef.current = true;
  };
  const handleDialogClose = () => {
    if (uploading || saving) return;
    if (dirtyRef.current && onSave) onSave();
    dirtyRef.current = false;
    handleClose();
  };

  const [tollChargeId, setTollChargeId] = useState(null);
  const [parkingChargeId, setParkingChargeId] = useState(null);

  const mapChargesToCards = (charges) =>
    (charges || []).flatMap((charge) =>
      (charge.TollAndParkingReceipts || []).map((receipt) => ({
        url: receipt.url,
        name: receipt.url ? receipt.url.split('/').pop() : 'Receipt',
        chargeId: charge.id,
        receiptId: receipt.id,
      })),
    );

  const unwrapChargesBody = (res) => {
    let data = res?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.body)) return data.body;
    if (Array.isArray(data?.data?.body)) return data.data.body;
    if (Array.isArray(data?.list)) return data.list;
    return [];
  };

  // ---- Step 1 & 2: load toll + parking charges for this trip ----
  const fetchTollParking = async () => {
    if (!tripId) return;
    try {
      const [tollRes, parkingRes] = await Promise.all([
        getTollChargesService({ tripId, type: 'Toll', page: 1, pageSize: 50 }),
        getTollChargesService({
          tripId,
          type: 'Parking',
          page: 1,
          pageSize: 50,
        }),
      ]);
      const tollBody = unwrapChargesBody(tollRes);
      const parkingBody = unwrapChargesBody(parkingRes);

      setTollList(mapChargesToCards(tollBody));
      setParkingList(mapChargesToCards(parkingBody));

      setTollChargeId(tollBody.length > 0 ? tollBody[0].id : null);
      setParkingChargeId(parkingBody.length > 0 ? parkingBody[0].id : null);
    } catch (error) {
      toast.error('Failed to load toll/parking attachments');
    }
  };

  useEffect(() => {
    if (open) {
      fetchTollParking();
    }
  }, [open, tripId]);

  const handlePreview = (url) => {
    if (url) {
      setSelectedImage(url);
      setPreviewModal(true);
    }
  };

  const handlePreviewClose = () => {
    setSelectedImage(null);
    setPreviewModal(false);
  };

  // ---- Step 4: remove one receipt (immediate, no approval) ----
  // Each card is one receipt. Removing it patches the charge with that
  // receipt id only. The backend refuses to leave a charge with no receipt,
  // so removing the last one deletes the charge record itself, as the old
  // single-file dialog did. Until 2026-09-23 every card removal deleted the
  // whole charge, taking every other receipt under it along.
  const handleRemove = async (type, index) => {
    if (isInvoiceGenerated) return;
    const list = type === 'toll' ? tollList : parkingList;
    const item = list[index];
    if (!item?.chargeId) return;

    const siblings = list.filter((x) => x.chargeId === item.chargeId);
    const isLastReceipt = siblings.length <= 1 || !item.receiptId;

    setSaving(true);
    const onDone = async () => {
      toast.success('Removed successfully');
      markDirty();
      await fetchTollParking();
      setSaving(false);
    };
    const onFail = (err) => {
      toast.error(err || 'Failed to remove');
      setSaving(false);
    };

    if (isLastReceipt) {
      dispatch(deleteTollCharge(item.chargeId, onDone, onFail));
      return;
    }

    const formData = new FormData();
    formData.append('tripId', tripId);
    formData.append('removeReceiptIds', item.receiptId);
    dispatch(updateTollCharge(item.chargeId, formData, onDone, onFail));
  };

  // The old per-card "re-upload" (replace one receipt in place) is gone: with
  // many receipts per charge, remove-then-add is the same thing and clearer.

  const handleNewUploadClick = (type) => {
    if (uploading || isInvoiceGenerated) return;
    setNewUploadType(type);
    newUploadInputRef.current?.click();
  };

  const handleNewFileChange = async (e) => {
    if (isInvoiceGenerated) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !newUploadType) return;

    const firstError = files.map(validateReceiptFile).find(Boolean);
    if (firstError) {
      toast.error(firstError);
      e.target.value = '';
      return;
    }

    setUploading(true);
    const apiType = newUploadType === 'toll' ? 'Toll' : 'Parking';
    const existingChargeId = newUploadType === 'toll' ? tollChargeId : parkingChargeId;
    const formData = new FormData();
    formData.append('tripId', tripId);
    files.forEach((file) => formData.append('receipts', file));

    if (existingChargeId) {
      // Row already exists: PATCH appends the new receipts. Until 2026-09-23
      // this also sent removeReceiptIds for the existing receipt, so every
      // upload replaced the previous one and a charge could never hold more
      // than a single file.
      dispatch(
        updateTollCharge(
          existingChargeId,
          formData,
          async () => {
            toast.success(
              `${files.length} ${apiType} receipt${files.length > 1 ? 's' : ''} uploaded successfully`,
            );
            markDirty();
            await fetchTollParking();
            setUploading(false);
            setNewUploadType(null);
          },
          (err) => {
            toast.error(err || 'Failed to upload');
            setUploading(false);
            setNewUploadType(null);
          },
        ),
      );
    } else {
      // Toll/Parking row does not exist, call the new POST API
      formData.append('type', apiType);
      dispatch(
        addTollCharge(
          formData,
          async () => {
            toast.success(
              `${files.length} ${apiType} receipt${files.length > 1 ? 's' : ''} uploaded successfully`,
            );
            markDirty();
            await fetchTollParking();
            setUploading(false);
            setNewUploadType(null);
          },
          (err) => {
            toast.error(err || 'Failed to upload');
            setUploading(false);
            setNewUploadType(null);
          },
        ),
      );
    }
    e.target.value = '';
  };

  // ---- Render Card ----
  const renderCard = (item, index, type) => {
    const url = item?.url || '';

    return (
      <Grid
        item
        md={3.7}
        sm={5.8}
        xs={12}
        className="reservationAttachmentCard borderStyle padding10 m10"
        key={`${type}-${index}`}
        onClick={() =>
          isImageUrl(url) ? handlePreview(url) : url && window.open(url, '_blank', 'noopener')
        }
      >
        {/* Image Preview, or a document label for PDF / Office receipts */}
        <div className="dlImgMain">
          <div className="dlImgBg"></div>
          <div className="attachmentPreviewLabel">
            {url && isImageUrl(url) ? (
              <img src={url} alt={`${type} ${index + 1}`} className="dlImg" />
            ) : url ? (
              <Box className="noPreview flex justifyCenter alignCenter">
                <Typography variant="body3" className="colorGrey">
                  {fileExtension(url.split('?')[0]).replace('.', '').toUpperCase() || 'FILE'}{' '}
                  document
                </Typography>
              </Box>
            ) : (
              <Box className="noPreview flex justifyCenter alignCenter">
                <Typography variant="body3" className="colorGrey">
                  No Preview
                </Typography>
              </Box>
            )}
          </div>
        </div>

        {/* Card Footer - Name + Actions */}
        <Grid container justifyContent="space-between" alignItems="center" className="padding10">
          <Grid item xs={7}>
            <Tooltip title={item?.name || ''} placement="bottom">
              <Typography variant="body3" className="colorBlack fw600">
                {truncateComment(item?.name || `${type} ${index + 1}`, 20)}
              </Typography>
            </Tooltip>
          </Grid>

          <Grid item xs={5} container justifyContent="flex-end" alignItems="center" spacing={1}>
            {/* Remove */}
            <Grid item>
              <Tooltip title="Remove" placement="top">
                <DeleteOutlineIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!saving && !isInvoiceGenerated) handleRemove(type, index);
                  }}
                  className="deleteIcon"
                  sx={{
                    opacity: saving || isInvoiceGenerated ? 0.4 : 1,
                    cursor: saving || isInvoiceGenerated ? 'not-allowed' : 'pointer',
                    pointerEvents: isInvoiceGenerated ? 'none' : 'auto',
                  }}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  // const renderSection = (title, items, type) => {
  //   if (!items || items.length === 0) {
  //     return (
  //       <Box className="mb20">
  //         <Typography variant="head6" className="fw600 colorBlack mb10">
  //           {title}
  //         </Typography>
  //         <Typography variant="body3" className="colorGrey">
  //           No {type} attachments for this trip.
  //         </Typography>
  //       </Box>
  //     );
  //   }
  //   return (
  //     <Box className="mb20">
  //       <Typography variant="head6" className="fw600 colorBlack mb10">
  //         {title} ({items.length})
  //       </Typography>
  //       <Grid
  //         container
  //         direction="row"
  //         justifyContent="flex-start"
  //         alignItems="start"
  //       >
  //         {items.map((item, idx) => renderCard(item, idx, type))}
  //       </Grid>
  //     </Box>
  //   );
  // };
  // The plus tile is the one way to add receipts; it sits last in the grid
  // and its label follows the state of the section.
  const renderUploadTile = (items, type) => {
    const busy = uploading && newUploadType === type;
    const locked = isInvoiceGenerated;
    const label = busy
      ? 'Uploading…'
      : items?.length > 0
        ? 'Upload more receipts'
        : 'Upload receipts';
    return (
      <Grid
        item
        md={3.7}
        sm={5.8}
        xs={12}
        className={`uploadTile m10 ${busy ? 'busy' : ''} ${locked ? 'locked' : ''}`}
        key={`${type}-upload`}
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-disabled={locked || uploading}
        aria-label={`${label} for ${type}`}
        onClick={() => handleNewUploadClick(type)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleNewUploadClick(type);
          }
        }}
      >
        <Box className="uploadTileInner">
          <Box className="uploadTilePlus">
            {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : <AddIcon />}
          </Box>
          <Typography variant="body3" className="fw600 colorBlack">
            {label}
          </Typography>
          <Typography variant="body6" className="colorGrey uploadTileHint">
            JPG, PNG, WebP, HEIC, PDF, DOC, DOCX, XLS, XLSX
            <br />
            Up to 10 MB each. Pick several at once.
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderSection = (title, items, type) => (
    <Box className="mb20">
      <Typography variant="head6" className="fw600 colorBlack mb10">
        {title}
        {items?.length > 0 && (
          <Typography component="span" variant="body4" className="colorGrey pl10">
            ({items.length})
          </Typography>
        )}
      </Typography>
      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch">
        {(items || []).map((item, idx) => renderCard(item, idx, type))}
        {renderUploadTile(items, type)}
      </Grid>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        aria-labelledby="reservation-attachments-dialog"
      >
        <DialogTitle className="flex justifyBetween alignCenter">
          <Typography variant="head5" className="fw600 colorBlack">
            Toll & Parking Attachments
          </Typography>
          <IconButton onClick={handleDialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isInvoiceGenerated && (
            <Box className="uploadLockNote mb20" role="status">
              <LockOutlinedIcon fontSize="small" />
              <Typography variant="body3">
                An invoice has been raised for this trip, so receipts can no longer be added or
                removed.
              </Typography>
            </Box>
          )}

          <input
            type="file"
            ref={newUploadInputRef}
            onChange={handleNewFileChange}
            accept={RECEIPT_ACCEPT}
            multiple
            style={{ display: 'none' }}
          />

          {renderSection('Toll receipts', tollList, 'toll')}
          <div className="dashedborder mb20"></div>
          {renderSection('Parking receipts', parkingList, 'parking')}
        </DialogContent>
      </Dialog>

      {/* Image Preview */}
      {previewModal && (
        <CommonImageModal
          open={previewModal}
          handleClose={handlePreviewClose}
          selectedImage={selectedImage}
        />
      )}
    </>
  );
};

export default ReservationAttachmentsDialog;
